import React, { useEffect, useState } from 'react';
import {
  Button,
  Text,
  Group,
  Stack,
  Select,
  Pagination,
  Modal,
  Textarea,
  ActionIcon,
  Alert,
  Card,
  Box,
  Menu,
  Progress,
  NumberInput,
  Anchor,
  Table,
  Image,
  AspectRatio,
  TextInput,
  Switch,
  Tooltip,
  SimpleGrid,
  Badge,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconFileText,
  IconPlus,
  IconEdit,
  IconTrash,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconDots,
  IconUpload,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFilter,
  IconChevronDown,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import AdminPageHeader from '../../components/AdminPageHeader';
import CountUpNumber from '../../components/animations/CountUpNumber';
import PageTransition from '../../components/animations/PageTransition';
import PageSkeleton from '../../components/ui/PageSkeleton';
import SearchInput from '../../components/ui/SearchInput';
import { useSidebar } from '../../contexts/SidebarContext';
import {
  materialEstudioService,
  MaterialEstudio,
  MaterialEstudioCreate,
  TIPOS_MATERIAL,
} from '../../services/materialEstudioService';

const emptyForm = (): MaterialEstudioCreate => ({
  titulo: '',
  descripcion: '',
  pdfUrl: '',
  thumbnailUrl: '',
  especialidad: '',
  tema: '',
  autor: '',
  tipo: 'GUIA',
  anio: undefined,
  tags: '',
  activo: true,
});

const AdminMaterialesPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const { collapsed: sidebarCollapsed } = useSidebar();
  const sidebarWidth = sidebarCollapsed ? 60 : 260;

  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };
  const textColor = dark ? '#e2e8f0' : '#2d2a26';
  const mutedColor = dark ? '#94a3b8' : '#8c8478';
  const colTipo = dark ? '#c8a8d6' : '#8a6aa0';
  const colEspecialidad = dark ? '#8aa9d6' : '#5c7bb0';
  const primaryBtn = { root: { backgroundColor: textColor, color: dark ? '#1e293b' : '#f7f3ee' } };
  const outlineBtn = { root: { borderColor: textColor, color: textColor, backgroundColor: 'transparent' } };

  const [materiales, setMateriales] = useState<MaterialEstudio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<string | null>(null);
  const [filterActivo, setFilterActivo] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [opened, { open, close }] = useDisclosure(false);
  const [previewOpened, { open: openPreview, close: closePreview }] = useDisclosure(false);
  const [previewMaterial, setPreviewMaterial] = useState<MaterialEstudio | null>(null);
  const [editing, setEditing] = useState<MaterialEstudio | null>(null);
  const [formData, setFormData] = useState<MaterialEstudioCreate>(emptyForm());
  const [formLoading, setFormLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      const data = await materialEstudioService.listarTodos();
      setMateriales(data);
      setError(null);
    } catch {
      setError('Error al cargar los materiales.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = materiales.filter((m) => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      !q ||
      m.titulo.toLowerCase().includes(q) ||
      (m.descripcion || '').toLowerCase().includes(q) ||
      (m.especialidad || '').toLowerCase().includes(q) ||
      (m.tema || '').toLowerCase().includes(q) ||
      (m.autor || '').toLowerCase().includes(q);
    const matchTipo = !filterTipo || m.tipo === filterTipo;
    const matchActivo = !filterActivo || (filterActivo === 'activos' ? m.activo : !m.activo);
    return matchSearch && matchTipo && matchActivo;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const hayFiltrosActivos = Boolean(searchTerm || filterTipo || filterActivo);

  const handleOpenForm = (m?: MaterialEstudio) => {
    if (m) {
      setEditing(m);
      setFormData({
        id: m.id,
        titulo: m.titulo,
        descripcion: m.descripcion || '',
        pdfUrl: m.pdfUrl,
        thumbnailUrl: m.thumbnailUrl || '',
        especialidad: m.especialidad || '',
        tema: m.tema || '',
        autor: m.autor || '',
        tipo: m.tipo || 'GUIA',
        anio: m.anio ?? undefined,
        tags: m.tags || '',
        activo: m.activo,
      });
    } else {
      setEditing(null);
      setFormData(emptyForm());
    }
    setUploadPct(0);
    open();
  };

  const handlePreview = (m: MaterialEstudio) => {
    setPreviewMaterial(m);
    openPreview();
  };

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      notifications.show({ title: 'Archivo inválido', message: 'Solo se permiten PDFs', color: 'red' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      notifications.show({ title: 'Archivo muy grande', message: 'El PDF supera 20 MB', color: 'red' });
      return;
    }
    try {
      setUploading(true);
      setUploadPct(0);
      const res = await materialEstudioService.uploadPdf(file, setUploadPct);
      setFormData((prev) => ({ ...prev, pdfUrl: res.url, thumbnailUrl: res.thumbnailUrl }));
      notifications.show({ title: 'PDF subido', message: 'Vista previa generada. Listo para guardar.', color: 'green' });
    } catch (e: any) {
      notifications.show({
        title: 'Error al subir',
        message: e?.response?.data?.message || 'No se pudo subir el PDF',
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.titulo.trim()) {
      notifications.show({ title: 'Error', message: 'El título es obligatorio', color: 'red' });
      return;
    }
    if (!formData.pdfUrl) {
      notifications.show({ title: 'Error', message: 'Sube el PDF antes de guardar', color: 'red' });
      return;
    }
    try {
      setFormLoading(true);
      await materialEstudioService.guardar(formData);
      notifications.show({
        title: 'Éxito',
        message: editing ? 'Material actualizado correctamente' : 'Material creado correctamente',
        color: 'green',
      });
      close();
      cargar();
    } catch (e: any) {
      notifications.show({
        title: 'Error',
        message: e?.response?.data?.message || 'No se pudo guardar el material',
        color: 'red',
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este material? El PDF también se borrará de la nube.')) return;
    try {
      await materialEstudioService.eliminar(id);
      notifications.show({ title: 'Éxito', message: 'Material eliminado correctamente', color: 'green' });
      cargar();
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudo eliminar el material', color: 'red' });
    }
  };

  const handleToggle = async (id: number) => {
    try {
      await materialEstudioService.toggleActivo(id);
      cargar();
    } catch {
      notifications.show({ title: 'Error', message: 'No se pudo cambiar el estado', color: 'red' });
    }
  };

  if (loading) return <PageSkeleton variant="grid" count={3} />;

  if (error) {
    return (
      <Box style={{ flex: 1, padding: '16px 24px' }}>
        <Alert icon={<IconInfoCircle size="1rem" />} title="Error" color="red">
          {error}
        </Alert>
      </Box>
    );
  }

  const statsCards = [
    { icon: IconFileText, value: materiales.length, label: 'Total materiales', duration: 2500 },
    { icon: IconCheck, value: materiales.filter((m) => m.activo).length, label: 'Activos', duration: 2000 },
    { icon: IconX, value: materiales.filter((m) => !m.activo).length, label: 'Inactivos', duration: 2000 },
  ];

  const tipoLabel = (v?: string | null) =>
    v ? (TIPOS_MATERIAL.find((t) => t.value === v)?.label || v) : '';

  return (
    <PageTransition type="medical" duration={800}>
      <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto' }}>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <AdminPageHeader
              title="Materiales"
              subtitle="PDFs, guías y protocolos para los estudiantes"
            />
            <Button leftSection={<IconPlus size={16} />} styles={primaryBtn} onClick={() => handleOpenForm()}>
              Nuevo material
            </Button>
          </Group>

          {/* Stats */}
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
            {statsCards.map((s) => (
              <Card key={s.label} withBorder radius="lg" p="lg" style={cardStyle}>
                <Stack gap="sm" align="stretch">
                  <s.icon size={20} color={textColor} style={{ flexShrink: 0 }} />
                  <Box style={{ minWidth: 0 }}>
                    <CountUpNumber
                      value={s.value}
                      duration={s.duration}
                      style={{ color: textColor, fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Space Grotesk, Inter, sans-serif', textAlign: 'left' }}
                    />
                    <Text size="xs" ta="left" style={{ color: mutedColor, fontFamily: 'Inter, sans-serif' }}>
                      {s.label}
                    </Text>
                  </Box>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>

          {/* Filtros */}
          <Group gap="sm" align="center" wrap="wrap">
            <SearchInput
              placeholder="Buscar por título, descripción, especialidad, tema o autor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1, minWidth: 220 }}
            />

            <Menu shadow="md" width={220} position="bottom-start" withinPortal>
              <Menu.Target>
                <Button
                  variant={filterTipo ? 'light' : 'default'}
                  color="gray"
                  radius="md"
                  leftSection={<IconFilter size={16} />}
                  rightSection={<IconChevronDown size={14} />}
                >
                  {filterTipo ? tipoLabel(filterTipo) : 'Tipo'}
                </Button>
              </Menu.Target>
              <Menu.Dropdown style={{ maxHeight: 320, overflowY: 'auto' }}>
                <Menu.Item
                  leftSection={!filterTipo ? <IconCheck size={14} /> : <Box style={{ width: 14 }} />}
                  onClick={() => setFilterTipo(null)}
                >
                  Tipo (todos)
                </Menu.Item>
                <Menu.Divider />
                {TIPOS_MATERIAL.map((t) => {
                  const sel = filterTipo === t.value;
                  return (
                    <Menu.Item key={t.value} onClick={() => setFilterTipo(t.value)} rightSection={sel ? <IconCheck size={14} /> : null}>
                      <Text size="sm" fw={sel ? 700 : 500}>{t.label}</Text>
                    </Menu.Item>
                  );
                })}
              </Menu.Dropdown>
            </Menu>

            <Menu shadow="md" width={200} position="bottom-start" withinPortal>
              <Menu.Target>
                <Button
                  variant={filterActivo ? 'light' : 'default'}
                  color="gray"
                  radius="md"
                  leftSection={<IconFilter size={16} />}
                  rightSection={<IconChevronDown size={14} />}
                >
                  {filterActivo === 'activos' ? 'Activos' : filterActivo === 'inactivos' ? 'Inactivos' : 'Estado'}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={!filterActivo ? <IconCheck size={14} /> : <Box style={{ width: 14 }} />}
                  onClick={() => setFilterActivo(null)}
                >
                  Estado (todos)
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item onClick={() => setFilterActivo('activos')} rightSection={filterActivo === 'activos' ? <IconCheck size={14} /> : null}>
                  <Text size="sm" fw={filterActivo === 'activos' ? 700 : 500}>Activos</Text>
                </Menu.Item>
                <Menu.Item onClick={() => setFilterActivo('inactivos')} rightSection={filterActivo === 'inactivos' ? <IconCheck size={14} /> : null}>
                  <Text size="sm" fw={filterActivo === 'inactivos' ? 700 : 500}>Inactivos</Text>
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>

            {hayFiltrosActivos && (
              <Button variant="subtle" color="gray" onClick={() => { setSearchTerm(''); setFilterTipo(null); setFilterActivo(null); }}>
                Limpiar
              </Button>
            )}
          </Group>

          {/* Tabla */}
          {filtered.length === 0 ? (
            <Card withBorder radius="lg" p="xl" style={cardStyle}>
              <Stack align="center" gap="md">
                <IconFileText size={48} style={{ opacity: 0.3, color: mutedColor }} />
                <Text style={{ color: mutedColor }}>
                  {hayFiltrosActivos ? 'Ningún material coincide con los filtros.' : 'No hay materiales. Crea el primero.'}
                </Text>
              </Stack>
            </Card>
          ) : (
            <Card withBorder radius="lg" p="lg" style={cardStyle}>
              <Table.ScrollContainer minWidth={720}>
                <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" withRowBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12, width: 64 }}>PDF</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Título</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Tipo</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Especialidad</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Año</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12 }}>Estado</Table.Th>
                      <Table.Th style={{ color: mutedColor, fontWeight: 600, fontSize: 12, width: 90, textAlign: 'right' }}>Acciones</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginated.map((m) => (
                      <Table.Tr key={m.id} style={{ cursor: 'pointer' }} onClick={() => handlePreview(m)}>
                        <Table.Td onClick={(e) => { e.stopPropagation(); handlePreview(m); }}>
                          <Box
                            style={{
                              width: 44,
                              height: 56,
                              borderRadius: 6,
                              overflow: 'hidden',
                              background: dark ? '#0f172a' : '#e9e4dc',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                            }}
                          >
                            {m.thumbnailUrl ? (
                              <Image src={m.thumbnailUrl} alt={m.titulo} fit="cover" w="100%" h="100%" />
                            ) : (
                              <IconFileText size={20} color={mutedColor} />
                            )}
                          </Box>
                        </Table.Td>
                        <Table.Td>
                          <Tooltip label={m.titulo} multiline w={420} withArrow position="top-start">
                            <Box>
                              <Text size="xs" fw={600} ta="left" style={{ color: textColor }}>
                                {m.titulo}
                              </Text>
                              {m.autor && (
                                <Text size="xs" ta="left" style={{ color: mutedColor }}>
                                  {m.autor}
                                </Text>
                              )}
                            </Box>
                          </Tooltip>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={500} ta="left" style={{ color: colTipo }}>
                            {tipoLabel(m.tipo) || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={500} ta="left" style={{ color: colEspecialidad }}>
                            {m.especialidad || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={500} ta="left" style={{ color: textColor }}>
                            {m.anio || '—'}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={m.activo ? 'green' : 'red'} variant="light" size="sm" radius="sm">
                            {m.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                          <Menu shadow="md" width={180} position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray" size="md" aria-label="Acciones">
                                <IconDots size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item leftSection={<IconEye size={14} />} onClick={() => handlePreview(m)}>
                                Vista previa
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconExternalLink size={14} />}
                                component="a"
                                href={m.pdfUrl}
                                target="_blank"
                              >
                                Abrir PDF
                              </Menu.Item>
                              <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleOpenForm(m)}>
                                Editar
                              </Menu.Item>
                              <Menu.Item
                                leftSection={m.activo ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                                onClick={() => handleToggle(m.id)}
                              >
                                {m.activo ? 'Desactivar' : 'Activar'}
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => handleDelete(m.id)}>
                                Eliminar
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>

              <Group justify="space-between" align="center" mt="md" wrap="wrap" gap="sm">
                <Group gap="xs" align="center">
                  <Text size="xs" style={{ color: mutedColor }}>
                    Mostrando {paginated.length} de {filtered.length.toLocaleString('es-MX')}
                  </Text>
                  <Select
                    size="xs"
                    w={92}
                    data={['10', '20', '50', '100']}
                    value={String(itemsPerPage)}
                    onChange={(v) => v && setItemsPerPage(Number(v))}
                    allowDeselect={false}
                    aria-label="Materiales por página"
                  />
                </Group>
                {totalPages > 1 && (
                  <Pagination value={page} onChange={setPage} total={totalPages} color="gray" size="sm" />
                )}
              </Group>
            </Card>
          )}
        </Stack>

        {/* Modal: vista previa del PDF.
            zIndex 1300 para quedar SOBRE el sidebar (z 900); paddingLeft del inner
            compensa el ancho del sidebar para que el modal quede centrado en el
            espacio útil. */}
        <Modal
          opened={previewOpened}
          onClose={closePreview}
          withinPortal
          zIndex={1300}
          size={`calc(100vw - ${sidebarWidth + 48}px)`}
          padding={0}
          radius="md"
          shadow="xl"
          withCloseButton={false}
          centered
          overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
          styles={{
            inner: { paddingLeft: sidebarWidth, paddingRight: 24 },
            content: {
              height: 'calc(100vh - 60px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
            body: {
              padding: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            },
          }}
        >
          {previewMaterial && (
            <>
              <Group
                justify="space-between"
                px="md"
                py="sm"
                wrap="nowrap"
                style={{
                  borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                  background: dark ? '#1e293b' : '#f7f3ee',
                  flexShrink: 0,
                }}
              >
                <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                  <IconFileText size={18} color={textColor} style={{ flexShrink: 0 }} />
                  <Box style={{ minWidth: 0 }}>
                    <Text fw={600} truncate style={{ color: textColor, fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                      {previewMaterial.titulo}
                    </Text>
                    {previewMaterial.autor && (
                      <Text size="xs" truncate style={{ color: mutedColor }}>
                        {previewMaterial.autor}
                      </Text>
                    )}
                  </Box>
                </Group>
                <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Button
                    component="a"
                    href={previewMaterial.pdfUrl}
                    target="_blank"
                    variant="default"
                    styles={outlineBtn}
                    size="xs"
                    leftSection={<IconExternalLink size={14} />}
                  >
                    Nueva pestaña
                  </Button>
                  <ActionIcon variant="subtle" color="gray" onClick={closePreview} aria-label="Cerrar">
                    <IconX size={18} />
                  </ActionIcon>
                </Group>
              </Group>
              <Box style={{ flex: 1, minHeight: '75vh', background: dark ? '#0f172a' : '#e5e7eb' }}>
                <iframe
                  src={previewMaterial.pdfUrl}
                  title={previewMaterial.titulo}
                  style={{ border: 0, width: '100%', height: '100%', display: 'block' }}
                />
              </Box>
            </>
          )}
        </Modal>

        {/* Modal: formulario */}
        <Modal
          opened={opened}
          onClose={close}
          title={editing ? 'Editar material' : 'Nuevo material'}
          size="lg"
          withinPortal
          zIndex={1300}
          centered
          radius="md"
          shadow="xl"
          styles={{ inner: { paddingLeft: sidebarWidth, paddingRight: 24 } }}
        >
          <Stack gap="md">
            <Box>
              <Text size="sm" fw={500} mb={6} style={{ color: textColor }}>
                PDF <span style={{ color: '#e03131' }}>*</span>
              </Text>
              <Group gap="sm" wrap="nowrap" align="center">
                <Button
                  component="label"
                  variant="default"
                  styles={outlineBtn}
                  leftSection={<IconUpload size={16} />}
                  loading={uploading}
                >
                  {formData.pdfUrl ? 'Reemplazar PDF' : 'Subir PDF'}
                  <input
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={(e) => handleUpload(e.target.files?.[0] || null)}
                  />
                </Button>
                {formData.pdfUrl && (
                  <Anchor href={formData.pdfUrl} target="_blank" size="sm" style={{ color: textColor }}>
                    Ver PDF subido
                  </Anchor>
                )}
              </Group>
              {uploading && <Progress mt="xs" value={uploadPct} color="gray" />}
              {formData.thumbnailUrl && (
                <Box mt="sm">
                  <Text size="xs" mb={4} style={{ color: mutedColor }}>Portada generada:</Text>
                  <AspectRatio ratio={4 / 3} maw={180}>
                    <Image src={formData.thumbnailUrl} radius="md" alt="Portada" />
                  </AspectRatio>
                </Box>
              )}
              <Text size="xs" mt={6} style={{ color: mutedColor }}>
                Máximo 20 MB. El PDF se sirve en línea para los estudiantes.
              </Text>
            </Box>

            <TextInput
              label="Título"
              placeholder="Ej. Guía ATLS 10ª edición"
              required
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
            />

            <Textarea
              label="Descripción"
              placeholder="Breve resumen del contenido"
              minRows={2}
              value={formData.descripcion || ''}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            />

            <Group grow>
              <Select
                label="Tipo"
                data={TIPOS_MATERIAL}
                value={formData.tipo || null}
                onChange={(v) => setFormData({ ...formData, tipo: v || undefined })}
              />
              <NumberInput
                label="Año"
                placeholder="2023"
                min={1900}
                max={2100}
                value={formData.anio ?? ''}
                onChange={(v) => setFormData({ ...formData, anio: typeof v === 'number' ? v : undefined })}
              />
            </Group>

            <Group grow>
              <TextInput
                label="Especialidad"
                placeholder="Ej. Urgencias"
                value={formData.especialidad || ''}
                onChange={(e) => setFormData({ ...formData, especialidad: e.target.value })}
              />
              <TextInput
                label="Tema"
                placeholder="Ej. Trauma"
                value={formData.tema || ''}
                onChange={(e) => setFormData({ ...formData, tema: e.target.value })}
              />
            </Group>

            <TextInput
              label="Autor / Institución"
              placeholder="Ej. American College of Surgeons"
              value={formData.autor || ''}
              onChange={(e) => setFormData({ ...formData, autor: e.target.value })}
            />

            <TextInput
              label="Tags (separados por coma)"
              placeholder="trauma, atls, urgencias"
              value={formData.tags || ''}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            />

            <Switch
              label="Material activo"
              checked={formData.activo !== false}
              onChange={(e) => setFormData({ ...formData, activo: e.currentTarget.checked })}
            />

            <Group justify="flex-end">
              <Button variant="default" styles={outlineBtn} onClick={close}>
                Cancelar
              </Button>
              <Button styles={primaryBtn} onClick={handleSubmit} loading={formLoading}>
                {editing ? 'Actualizar' : 'Crear'}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </PageTransition>
  );
};

export default AdminMaterialesPage;
